import { backdropContainer, backdropImage } from 'assets/jss/material-dashboard-pro-react';
import { useAdCampaignDetail } from 'data/ad-campaign';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { EAdCampaignStatus } from 'types/ad-campaign';

import { ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import PaymentIcon from '@material-ui/icons/Payment';
import ScheduleIcon from '@material-ui/icons/Schedule';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import { ADS_DASHBOARD_ROUTE, useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import ProductBillingTab from 'components/Billing/ProductBillingTab';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import DashboardSection from 'components/DashboardSection/DashboardSection';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { updateAdCampaign } from 'utils/api/ad-campaigns';
import { showAPIErrorAlert } from 'utils/lib';

import AdCampaignGeneralTab from './AdCampaignGeneralTab';
import AdCampaignLeadsTab from './AdCampaignLeadsTab';
import AdCampaignPauseResumeButton from './AdCampaignPauseResumeButton';
import AdCampaignSettingsTab from './AdCampaignSettingsTab';

const useStyles = makeStyles((theme) => ({
  backdropContainer,
  backdropImage,
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,

    '& h4': {
      fontWeight: 'bold',
      marginBottom: '4px',
      fontSize: '14px',
      lineHeight: '18px',
      color: theme.palette.black.main,
    },
  },

  statusBanner: {
    margin: '20px 0 12px',
  },

  infoContainer: {
    marginTop: '20px',
    '& > div': {
      marginBottom: '30px',
    },
  },

  adminInfo: {
    padding: '32px 16px',
    fontWeight: 400,
    fontSize: '14px',
  },

  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
  linkClicks: {
    display: 'flex',
    alignItems: 'center',
    '& a': {
      marginLeft: '5px',
      color: theme.palette.orange.main,
      textDecoration: 'underline',
    },
  },
}));

const tabs = [
  {
    name: 'General',
    slug: '',
  },
  {
    name: 'Leads',
    slug: 'leads',
  },
  {
    name: 'Billing',
    slug: 'billing',
  },
  {
    name: 'Settings',
    slug: 'settings',
  },
];

// Fallback to touches tab as the default in either case
const getSlugForTab = (s: string | undefined) => tabs.find(({ name }) => name === s)?.slug ?? '';
const getTabForSlug = (s: string | undefined) => tabs.find(({ slug }) => slug === s)?.name ?? 'General';

interface RouteParams {
  adCampaignId: string;
  slug?: string;
}

export default () => {
  const { adCampaignId, slug } = useParams<RouteParams>();

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { isAnyAgent } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any; // TODO: TEMP AS ANY

  const [activeTab, setActiveTab] = React.useState(getTabForSlug(slug));

  React.useEffect(() => {
    setActiveTab(getTabForSlug(slug));
  }, [slug]);

  if (!adCampaignId) {
    // Invalid url params. Redirect to dashboard
    history.replace(ADS_DASHBOARD_ROUTE);
  }

  const {
    adCampaign, isLoadingAdCampaign, isErrorAdCampaign, mutateAdCampaign,
  } = useAdCampaignDetail(adCampaignId);

  let tabsToShow = tabs.map(({ name }) => name);

  if (adCampaign) {
    // Don't show the leads tab for digital ads without custom call to action links
    if (adCampaign.useCustomCallToActionLink) {
      tabsToShow = tabsToShow.filter((e) => e !== 'Leads');
    }

    // Don't show the settings tab for cancelled digital ads and non agents
    if (!isAnyAgent || ![EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active, EAdCampaignStatus.Paused].includes(adCampaign.status)) {
      tabsToShow = tabsToShow.filter((e) => e !== 'Settings');
    }

    if (!isAnyAgent) tabsToShow = tabsToShow.filter((e) => e !== 'Billing');
  }

  const resolvedActiveTab = tabsToShow.includes(activeTab) ? activeTab : tabsToShow[0] ?? 'General';

  React.useEffect(() => {
    if (activeTab !== resolvedActiveTab) {
      setActiveTab(resolvedActiveTab);
    }
  }, [activeTab, resolvedActiveTab]);

  React.useEffect(() => {
    const resolvedRoute = `/dashboard/ad-campaigns/${adCampaignId}/${getSlugForTab(resolvedActiveTab)}`;
    if (location.pathname !== resolvedRoute) {
      if (!slug || activeTab !== resolvedActiveTab) {
        history.replace(resolvedRoute);
      } else {
        history.push(resolvedRoute);
      }
    }
  }, [activeTab, adCampaignId, history, location.pathname, resolvedActiveTab, slug]);

  React.useEffect(() => {
    if (isErrorAdCampaign) {
      // Likely caused by an invalid campaign id in the url params. Redirect to dashboard
      history.push(ADS_DASHBOARD_ROUTE);
      console.error(isErrorAdCampaign);
    }
  }, [isErrorAdCampaign]);

  const onUpdateAdCampaignName = async (name: string) => {
    showLoadingIndicatorModal();
    try {
      await updateAdCampaign(adCampaignId, { name });

      mutateAdCampaign({
        data: {
          ...adCampaign,
          name,
        },
      }, { revalidate: false });

      toast.success('Ad name updated');
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  if (isLoadingAdCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  let tabContent;

  if (resolvedActiveTab === 'General') {
    tabContent = (<AdCampaignGeneralTab adCampaignId={adCampaignId} />);
  } else if (resolvedActiveTab === 'Leads') {
    tabContent = (<AdCampaignLeadsTab adCampaignId={adCampaignId} />);
  } else if (resolvedActiveTab === 'Billing') {
    tabContent = (
      <ProductBillingTab
        type="ad-campaign"
        id={adCampaign._id}
        paymentMethodId={adCampaign.paymentMethodId}
        amount={adCampaign.costs?.userMonthlyBudget}
        cadence="Monthly"
        nextBillingDate={adCampaign.costs?.nextInvoiceDate}
        editable
        onUpdated={() => mutateAdCampaign()}
      />
    );
  } else if (resolvedActiveTab === 'Settings') {
    tabContent = (<AdCampaignSettingsTab adCampaignId={adCampaignId} />);
  }

  return (
    <div className={classes.root}>
      <DashboardSection
        breadcrumbs={[{ name: 'Digital Ads', to: (location.state as any)?.previousPathname ?? ADS_DASHBOARD_ROUTE }]}
        sectionName={adCampaign.name}
        sectionNameEditable={isAnyAgent}
        onSectionEditName={onUpdateAdCampaignName}
        tabs={tabsToShow}
        activeTab={resolvedActiveTab}
        setActiveTab={setActiveTab}
        buttonLayout="right"
      />

      {adCampaign.status === EAdCampaignStatus.PendingBilling && (
        <DashboardBubble
          variant="status"
          tone="warning"
          rootClass={classes.statusBanner}
          icon={<PaymentIcon />}
          title="Payment pending"
          content="We’re waiting for the first invoice payment before starting this digital ad."
          action={isAnyAgent && resolvedActiveTab !== 'Billing' ? (
            <ButtonBase onClick={() => setActiveTab('Billing')}>
              View billing
            </ButtonBase>
          ) : undefined}
        />
      )}

      {adCampaign.status === EAdCampaignStatus.PendingMarketingProfile && (
        <DashboardBubble
          variant="status"
          tone="warning"
          rootClass={classes.statusBanner}
          icon={<WarningRoundedIcon />}
          title="Complete your marketing profile"
          content="Your digital ad can’t start delivering until your marketing profile is complete."
          action={isAnyAgent ? (
            <ButtonBase onClick={() => history.push('/dashboard/marketing-profile')}>
              Complete profile
            </ButtonBase>
          ) : undefined}
        />
      )}

      {adCampaign.status === EAdCampaignStatus.PendingAdApproval && (
        <DashboardBubble
          variant="status"
          tone="info"
          rootClass={classes.statusBanner}
          icon={<ScheduleIcon />}
          title="Ad review in progress"
          content="The ad networks are reviewing your creative. Delivery will begin automatically after approval."
        />
      )}

      {adCampaign.status === EAdCampaignStatus.Paused && (
        <DashboardBubble
          variant="status"
          tone="neutral"
          rootClass={classes.statusBanner}
          icon={<PauseCircleOutlineIcon />}
          title="Digital ad paused"
          content="This ad is paused. Resume it when you’re ready to continue delivery."
          action={isAnyAgent ? (
            <AdCampaignPauseResumeButton adCampaignId={adCampaignId} variant="banner" />
          ) : undefined}
        />
      )}

      {tabContent}
    </div>
  );
};
